import type { TicketGroup } from '@interfaces/ticketevolution/tikets';

type TicketPriceListing = Pick<TicketGroup, 'retail_price'>;

export type TicketPriceBucket = {
  count: number;
  end: number;
  label: string;
  start: number;
};

export type TicketPriceRange = {
  max: number;
  min: number;
};

const FALLBACK_PRICE_RANGE: TicketPriceRange = { min: 0, max: 100 };

function getTicketPrices(tickets: TicketPriceListing[]) {
  return tickets
    .map(({ retail_price }) => retail_price)
    .filter((price) => Number.isFinite(price));
}

export function getTicketPriceRange(
  tickets: TicketPriceListing[]
): TicketPriceRange {
  const prices = getTicketPrices(tickets);

  if (!prices.length) {
    return FALLBACK_PRICE_RANGE;
  }

  const min = Math.floor(Math.min(...prices));
  const max = Math.ceil(Math.max(...prices));

  return { min, max: max === min ? min + 1 : max };
}

export function getTicketPriceDistribution(
  tickets: TicketPriceListing[],
  range: TicketPriceRange,
  bucketCount = 12,
  formatPrice: (price: number) => string = (price) =>
    `$${price.toLocaleString()}`
): TicketPriceBucket[] {
  const safeBucketCount = Math.max(1, Math.floor(bucketCount));
  const bucketWidth = (range.max - range.min) / safeBucketCount;
  const buckets = Array.from({ length: safeBucketCount }, (_, index) => {
    const start = range.min + bucketWidth * index;
    const end = index === safeBucketCount - 1 ? range.max : start + bucketWidth;

    return {
      count: 0,
      start,
      end,
      label: `${formatPrice(Math.floor(start))}–${formatPrice(Math.ceil(end))}`,
    };
  });

  getTicketPrices(tickets).forEach((price) => {
    if (price < range.min || price > range.max) {
      return;
    }

    const bucketIndex = Math.min(
      Math.floor((price - range.min) / bucketWidth),
      safeBucketCount - 1
    );
    buckets[bucketIndex].count += 1;
  });

  return buckets;
}

export function getTicketsInPriceRange(
  tickets: TicketPriceListing[],
  range: number[]
) {
  const [min = 0, max = 0] = range;

  return getTicketPrices(tickets).filter(
    (price) => price >= min && price <= max
  ).length;
}
